/**
 * Device Detection Utility for Enhanced Snapchat Tracking
 * Provides device model and OS information for better ad matching
 */

export interface DeviceInfo {
  device_model?: string
  os_version?: string
  user_agent: string
}

/**
 * Extract device model from user agent
 */
export function getDeviceModel(): string | undefined {
  const userAgent = navigator.userAgent.toLowerCase()

  // iPhone detection
  const iphoneMatch = userAgent.match(/iphone\s*os\s*(\d+)[_.](\d+)/)
  if (iphoneMatch) {
    return `iPhone (iOS ${iphoneMatch[1]}.${iphoneMatch[2]})`
  }

  // iPad detection
  if (userAgent.includes('ipad')) {
    return 'iPad'
  }

  // Android device detection
  const androidMatch = userAgent.match(/android[;\s]([^;)]+)/)
  if (androidMatch) {
    const deviceInfo = androidMatch[1].trim().replace(/[;)].*/g, '')
    return `Android (${deviceInfo})`
  }

  // Windows detection
  if (userAgent.includes('windows')) {
    return 'Windows Desktop'
  }

  // Mac detection
  if (userAgent.includes('macintosh') || userAgent.includes('mac os x')) {
    return 'MacOS Desktop'
  }

  // Linux detection
  if (userAgent.includes('linux')) {
    return 'Linux Desktop'
  }

  return undefined
}

/**
 * Extract OS version from user agent
 */
export function getOSVersion(): string | undefined {
  const userAgent = navigator.userAgent.toLowerCase()

  // iOS version
  const iosMatch = userAgent.match(/os\s*(\d+)[_.](\d+)[_.]?(\d+)?/)
  if (iosMatch && (userAgent.includes('iphone') || userAgent.includes('ipad'))) {
    let version = `${iosMatch[1]}.${iosMatch[2]}`
    if (iosMatch[3]) {
      version += `.${iosMatch[3]}`
    }
    return `iOS ${version}`
  }

  // Android version
  const androidMatch = userAgent.match(/android\s*(\d+(?:\.\d+)*)/)
  if (androidMatch) {
    return `Android ${androidMatch[1]}`
  }

  // Windows version
  const winMatch = userAgent.match(/windows nt\s*(\d+\.\d+)/)
  if (winMatch) {
    const versionMap: Record<string, string> = {
      '10.0': 'Windows 10/11',
      '6.3': 'Windows 8.1',
      '6.2': 'Windows 8',
      '6.1': 'Windows 7'
    }
    return versionMap[winMatch[1]] || `Windows ${winMatch[1]}`
  }

  // macOS version
  const macMatch = userAgent.match(/mac os x\s*(\d+)[_.](\d+)[_.]?(\d+)?/)
  if (macMatch) {
    let version = `${macMatch[1]}.${macMatch[2]}`
    if (macMatch[3]) {
      version += `.${macMatch[3]}`
    }
    return `macOS ${version}`
  }

  return undefined
}

/**
 * Get comprehensive device information
 */
export function getDeviceInfo(): DeviceInfo {
  return {
    device_model: getDeviceModel(),
    os_version: getOSVersion(),
    user_agent: navigator.userAgent
  }
}

/**
 * Check if phone number is available from browser autofill or other sources
 */
export function hasPaymentInfoAvailable(): boolean {
  // Check for stored payment methods (basic detection)
  try {
    // Check if browser has payment request API support
    if ('PaymentRequest' in window) {
      return true
    }

    // Check for common payment autofill patterns
    const forms = document.querySelectorAll('form')
    for (let i = 0; i < forms.length; i++) {
      const form = forms[i]
      const paymentInputs = form.querySelectorAll('input[autocomplete*="cc-"], input[name*="card"], input[name*="payment"]')
      if (paymentInputs.length > 0) {
        return true
      }
    }
  } catch (error) {
    // Silently fail - this is just for enhancement
  }

  return false
}